import React, { useState, useEffect } from 'react';

interface PollTimerProps {
  votingStart: string; // ISO 8601 date string
  votingEnd: string; // ISO 8601 date string
  status: 'active' | 'closed';
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number; // Total milliseconds
}

const PollTimer: React.FC<PollTimerProps> = ({ votingStart, votingEnd, status }) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [timerType, setTimerType] = useState<'starting' | 'ending' | 'closed'>('starting');

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const startTime = new Date(votingStart).getTime();
      const endTime = new Date(votingEnd).getTime();

      // Determine what we're counting down to
      if (now < startTime) {
        // Poll hasn't started yet - countdown to start
        setTimerType('starting');
        const diff = startTime - now;
        return calculateTimeBreakdown(diff);
      } else if (now >= startTime && now < endTime) {
        // Poll is active - countdown to end
        setTimerType('ending');
        const diff = endTime - now;
        return calculateTimeBreakdown(diff);
      } else {
        // Poll has ended
        setTimerType('closed');
        return null;
      }
    };

    const calculateTimeBreakdown = (milliseconds: number): TimeRemaining => {
      const seconds = Math.floor((milliseconds / 1000) % 60);
      const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
      const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
      const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

      return {
        days,
        hours,
        minutes,
        seconds,
        total: milliseconds,
      };
    };

    // Calculate immediately
    const initialTime = calculateTimeRemaining();
    setTimeRemaining(initialTime);

    // Update every second
    const interval = setInterval(() => {
      const time = calculateTimeRemaining();
      setTimeRemaining(time);
      
      // If time has expired, stop the interval and trigger a refresh
      if (time === null || time.total <= 0) {
        clearInterval(interval);
        // Trigger a custom event to refresh poll data
        window.dispatchEvent(new CustomEvent('pollTimerExpired'));
      }
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [votingStart, votingEnd]);

  // Don't show timer if poll is closed
  if (status === 'closed' || timerType === 'closed' || !timeRemaining || timeRemaining.total <= 0) {
    return null;
  }

  const { days, hours, minutes, seconds } = timeRemaining;

  // Determine timer label and styling
  const isStarting = timerType === 'starting';
  const timerLabel = isStarting ? 'Starts in' : 'Ends in';
  const timerClass = isStarting ? 'poll-timer-starting' : 'poll-timer-ending';

  return (
    <div className={`poll-timer ${timerClass}`}>
      <div className="poll-timer-header">
        <i className={`ri-${isStarting ? 'time-line' : 'alarm-warning-line'} poll-timer-icon`}></i>
        <span className="poll-timer-label">{timerLabel}</span>
      </div>
      <div className="poll-timer-display">
        {days > 0 && (
          <div className="poll-timer-unit">
            <span className="poll-timer-value">{String(days).padStart(2, '0')}</span>
            <span className="poll-timer-unit-label">Day{days !== 1 ? 's' : ''}</span>
          </div>
        )}
        <div className="poll-timer-unit">
          <span className="poll-timer-value">{String(hours).padStart(2, '0')}</span>
          <span className="poll-timer-unit-label">Hour{hours !== 1 ? 's' : ''}</span>
        </div>
        <div className="poll-timer-separator">:</div>
        <div className="poll-timer-unit">
          <span className="poll-timer-value">{String(minutes).padStart(2, '0')}</span>
          <span className="poll-timer-unit-label">Min{minutes !== 1 ? 's' : ''}</span>
        </div>
        <div className="poll-timer-separator">:</div>
        <div className="poll-timer-unit">
          <span className="poll-timer-value">{String(seconds).padStart(2, '0')}</span>
          <span className="poll-timer-unit-label">Sec{seconds !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default PollTimer;

